import { GitBranch, RefreshCw, Plus } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { SearchInput } from "../ui/search-input";
import { ResizablePanel } from "../ui/resizable";
import { FlowItem } from "./FlowItem";

interface Flow {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  status: string;
}

interface FlowsSidebarProps {
  flows: Flow[];
  filteredFlows: Flow[];
  selectedFlowId?: string;
  fetchingFlows: boolean;
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;
  onRefresh: () => void;
  onSelectFlow: (flowId: string) => void;
  onCreateFlow: () => void;
  canCreate: boolean;
}

export function FlowsSidebar({
  flows,
  filteredFlows,
  selectedFlowId,
  fetchingFlows,
  searchQuery,
  onSearchQueryChange,
  onRefresh,
  onSelectFlow,
  onCreateFlow,
  canCreate,
}: FlowsSidebarProps) {
  return (
    <ResizablePanel defaultSize={35} minSize={20} maxSize={55}>
      <div className="h-full flex flex-col border-r border-border bg-background">
        <div className="px-4 py-4 border-b border-border bg-background space-y-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <GitBranch className="h-3 w-3 text-muted-foreground" />
              <h2 className="text-xs font-semibold text-foreground">Flows</h2>
              <Badge variant="secondary" className="text-xs font-mono">
                {flows.length}
              </Badge>
            </div>
            <Button
              onClick={onRefresh}
              variant="ghost"
              size="sm"
              disabled={fetchingFlows}
              title="Refresh"
            >
              <RefreshCw
                className={`h-3 w-3 ${fetchingFlows ? "animate-spin" : ""} cursor-pointer`}
              />
            </Button>
          </div>
          {/* New Flow Button */}
          <Button
            onClick={onCreateFlow}
            variant="default"
            size="sm"
            className="w-full"
            disabled={!canCreate}
          >
            <Plus className="h-3 w-3 mr-2" />
            New Flow
          </Button>
          <SearchInput
            value={searchQuery}
            onValueChange={onSearchQueryChange}
            placeholder="Search flows by name or slug"
          />
        </div>

        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-2 pb-16">
              {fetchingFlows ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center mb-3">
                    <RefreshCw className="h-4 w-4 text-muted-foreground animate-spin cursor-pointer" />
                  </div>
                  <p className="text-xs text-muted-foreground font-semibold mb-1">
                    Loading flows...
                  </p>
                  <p className="text-xs text-muted-foreground/70">
                    Fetching flows from database
                  </p>
                </div>
              ) : filteredFlows.length === 0 ? (
                <div className="text-center py-8">
                  {flows.length === 0 ? (
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mb-3">
                        <GitBranch className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        No flows yet
                      </p>
                      <Button
                        onClick={onCreateFlow}
                        variant="outline"
                        size="sm"
                        disabled={!canCreate}
                      >
                        <Plus className="h-3 w-3 mr-2" />
                        Create your first flow
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No flows match your search.
                    </p>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2">
                  {filteredFlows.map((flow) => (
                    <FlowItem
                      key={flow._id}
                      flow={flow}
                      isSelected={selectedFlowId === flow._id}
                      onClick={() => onSelectFlow(flow._id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
    </ResizablePanel>
  );
}
